// MUI Components
import { Alert, Box, Button, CircularProgress } from '@mui/material'
import { DataGridPro, GridColDef, GridRenderCellParams, GridSortModel, useGridApiRef } from '@mui/x-data-grid-pro'

// React & React Router
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

// Redux
import { useDispatch, useSelector } from 'react-redux'
import { TimesheetStoreDispatch, TimesheetStoreState } from '../../store/timesheetStore'
import { Filters, addFilters } from '../../store/slices/timesheet/timesheetDetailSlice'

// API Hooks
import { useGetTimesheetQuery } from '../../utils/hooks/useGetTimesheetQuery'
import { useGetBranchesQuery } from '../../store/api/timesheet/timesheetBranchesApi'

// Custom Hooks
import { changeLoading } from '../../store/slices/function/timesheetLoaderSlice'
import { timesheetDetailApi } from '../../store/api/timesheet/timesheetDetailApi'
import { useGetTimesheetsFilteredQuery } from '../../utils/hooks/useGetTimesheetsFilteredQuery'

// Components
import { TableMotorista, TableData, InternalNumberCell } from '../general/TableFunctions'

// Types
import { TimesheetResponse } from '../../types/timesheetsAPI'

// DataGridPro v7 standard density defaults — used to estimate whether loaded rows
// fill the visible grid area without requiring internal scroll.
const DATAGRID_ROW_HEIGHT = 48
const DATAGRID_COLUMN_HEADER_HEIGHT = 56

const fieldToApiColumn: Record<string, Filters['orderByColumn']> = {
	driverName: 'DriverName',
	internalNumber: 'InternalNumber',
	timesheetDate: 'TimeSheetDate',
}

const apiColumnToField: Record<Filters['orderByColumn'], string> = {
	DriverName: 'driverName',
	InternalNumber: 'internalNumber',
	TimeSheetDate: 'timesheetDate',
	DriverId: 'driverId',
}

function TableMotoristas(): React.JSX.Element {
	const dispatch = useDispatch<TimesheetStoreDispatch>()
	const navigate = useNavigate()
	const timesheetDetails = useSelector((state: TimesheetStoreState) => state.timesheetDetails)
	const [searchParams] = useSearchParams()
	const { id } = useParams()

	const {
		data: timesheetsFilteredPageDetail,
		isFetching: isFetchingTimesheetsFiltered,
		isError,
		refetch,
	} = useGetTimesheetsFilteredQuery()

	const { isFetching: isFetchingTimesheet } = useGetTimesheetQuery()
	const { isLoading: isLoadingBranches } = useGetBranchesQuery()

	const apiRef = useGridApiRef()
	const [allItems, setAllItems] = useState<TimesheetResponse[]>([])
	const gridContainerRef = useRef<HTMLDivElement>(null)
	const currentPageRef = useRef(timesheetDetails.filters.currentPage ?? 1)
	currentPageRef.current = timesheetDetails.filters.currentPage ?? 1
	// Prevents auto-load from dispatching a second time before isFetchingTimesheetsFiltered catches up
	const autoLoadDispatchedRef = useRef(false)

	// Reset grid and currentPage when any non-pagination filter changes
	const prevNonPageFiltersRef = useRef<string | null>(null)
	useEffect(() => {
		const rest: Omit<Filters, 'currentPage' | 'pageSize'> = {
			orderByColumn: timesheetDetails.filters.orderByColumn,
			ascending: timesheetDetails.filters.ascending,
			startDate: timesheetDetails.filters.startDate,
			endDate: timesheetDetails.filters.endDate,
			driverId: timesheetDetails.filters.driverId,
			status: timesheetDetails.filters.status,
			branchId: timesheetDetails.filters.branchId,
		}
		const key = JSON.stringify(rest)
		if (prevNonPageFiltersRef.current !== null && prevNonPageFiltersRef.current !== key) {
			setAllItems([])
			dispatch(addFilters({ currentPage: 1 }))
		}
		prevNonPageFiltersRef.current = key
	}, [
		timesheetDetails.filters.orderByColumn,
		timesheetDetails.filters.ascending,
		timesheetDetails.filters.startDate,
		timesheetDetails.filters.endDate,
		timesheetDetails.filters.driverId,
		timesheetDetails.filters.status,
		timesheetDetails.filters.branchId,
	])

	useEffect(() => {
		const items = timesheetsFilteredPageDetail?.itemList ?? []
		if (items.length === 0 && (timesheetsFilteredPageDetail?.pageNumber ?? 0) === 0) return
		if ((timesheetsFilteredPageDetail?.pageNumber ?? 1) === 1) {
			setAllItems(items)
			apiRef.current?.scroll?.({ top: 0 })
		} else {
			setAllItems(prev => [...prev, ...items])
		}
	}, [timesheetsFilteredPageDetail])

	useEffect(() => {
		refetch()
	}, [refetch])

	// --- Why we don't use onRowsScrollEnd ---
	// DataGridPro with rowCount pre-sizes its virtual scroller to totalResults × rowHeight
	// (e.g. 100 rows × 52px = 5200px). onRowsScrollEnd only fires when the scroll position
	// reaches that full height minus the threshold — but the user can only scroll as far as
	// the loaded rows (e.g. 20 × 52 = 1040px), so the trigger is never reached naturally.
	//
	// --- What we do instead ---
	// Two complementary mechanisms, both guarded by autoLoadDispatchedRef to prevent
	// double-dispatch between renders while isFetchingTimesheetsFiltered catches up:
	//
	// 1. checkFits — if loaded rows fit entirely in the visible area (no scroll needed at all),
	//    dispatch the next page immediately. ResizeObserver retries this once the browser
	//    resolves the flex layout (clientHeight is 0 on the initial render).
	//
	// 2. handleScroll — scroll listener on .MuiDataGrid-virtualScroller that fires when
	//    scrollTop + clientHeight >= loadedRows × rowHeight - 150, i.e. the user reached
	//    the bottom of the LOADED content, regardless of the pre-sized virtual space beyond.
	useEffect(() => {
		if (isFetchingTimesheetsFiltered) {
			// Reset on every new fetch so the next page can be dispatched after it completes.
			autoLoadDispatchedRef.current = false
			return
		}
		if (allItems.length === 0) return
		if (allItems.length >= (timesheetsFilteredPageDetail?.totalResults ?? 0)) return

		const container = gridContainerRef.current
		if (!container) return

		const tryDispatch = () => {
			if (autoLoadDispatchedRef.current) return
			autoLoadDispatchedRef.current = true
			dispatch(addFilters({ currentPage: currentPageRef.current + 1 }))
		}

		// Mechanism 1: auto-load when all loaded rows fit without scrolling.
		const checkFits = () => {
			if (autoLoadDispatchedRef.current) return
			if (container.clientHeight === 0) return
			const availableRowsHeight = container.clientHeight - DATAGRID_COLUMN_HEADER_HEIGHT
			if (allItems.length * DATAGRID_ROW_HEIGHT <= availableRowsHeight) {
				tryDispatch()
			}
		}

		checkFits()

		// Mechanism 2: load next page when scroll reaches the bottom of loaded rows.
		const scroller = container.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement | null
		const handleScroll = scroller
			? () => {
					if (autoLoadDispatchedRef.current) return
					const loadedContentBottom = allItems.length * DATAGRID_ROW_HEIGHT
					if (scroller.scrollTop + scroller.clientHeight >= loadedContentBottom - 150) {
						tryDispatch()
					}
				}
			: null

		if (scroller && handleScroll) scroller.addEventListener('scroll', handleScroll)

		// ResizeObserver retries checkFits once the flex layout assigns a concrete height.
		const observer = new ResizeObserver(checkFits)
		observer.observe(container)

		return () => {
			if (scroller && handleScroll) scroller.removeEventListener('scroll', handleScroll)
			observer.disconnect()
		}
	}, [allItems.length, isFetchingTimesheetsFiltered, timesheetsFilteredPageDetail?.totalResults, dispatch])

	useEffect(() => {
		return () => {
			dispatch(addFilters({ currentPage: 1 }))
		}
	}, [timesheetDetails.filters.orderByColumn, timesheetDetails.filters.ascending, dispatch])

	const handleSortModelChange = useCallback(
		(model: GridSortModel) => {
			// When DataGridPro tries to clear the sort (3rd click: asc→desc→none),
			// cycle back to ascending on the current column instead of removing sort.
			if (model.length === 0) {
				dispatch(
					addFilters({
						orderByColumn: timesheetDetails.filters.orderByColumn,
						ascending: true,
						currentPage: 1,
						pageSize: 20,
					}),
				)
				return
			}
			const { field, sort } = model[0]
			const apiColumn = fieldToApiColumn[field] ?? 'TimeSheetDate'
			dispatch(
				addFilters({
					orderByColumn: apiColumn,
					ascending: sort === 'asc',
					currentPage: 1,
					pageSize: 20,
				}),
			)
		},
		[dispatch, timesheetDetails.filters.orderByColumn],
	)

	const sortModel = useMemo<GridSortModel>(
		() => [
			{
				field: apiColumnToField[timesheetDetails.filters.orderByColumn] ?? 'timesheetDate',
				sort: timesheetDetails.filters.ascending ? 'asc' : 'desc',
			},
		],
		[timesheetDetails.filters.orderByColumn, timesheetDetails.filters.ascending],
	)

	const handleNewTimeSheet = useCallback(
		(item: TimesheetResponse) => {
			dispatch(timesheetDetailApi.util.invalidateTags([{ type: 'TimesheetDetail', id: item.timeSheetId }]))
			dispatch(changeLoading({ loading: true }))
			navigate(`/timesheet/${item.timeSheetId}?${searchParams.toString()}`)
		},
		[dispatch, navigate, searchParams],
	)

	const columns = useMemo<GridColDef<TimesheetResponse>[]>(
		() => [
			{
				field: 'driverName',
				headerName: 'Motorista / Estado',
				flex: 1,
				align: 'left',
				headerAlign: 'left',
				width: 150,
				sortable: true,
				renderCell: (params: GridRenderCellParams<TimesheetResponse>) => (
					<TableMotorista
						img='' // TODO: add driver image when available
						name={params.row.driverName}
						status={params.row.status}
					/>
				),
			},
			{
				field: 'internalNumber',
				headerName: 'Nr',
				width: 72,
				sortable: true,
				align: 'left',
				headerAlign: 'left',
				renderCell: (params: GridRenderCellParams<TimesheetResponse>) => (
					<InternalNumberCell internalNumber={params.row.internalNumber} />
				),
			},
			{
				field: 'timesheetDate',
				headerName: 'Data / Serv',
				flex: 1,
				colSpan: 2,
				width: 170,
				sortable: true,
				renderCell: (params: GridRenderCellParams<TimesheetResponse>) => (
					<TableData
						date={params.row.timesheetDate}
						services={params.row.totalServices}
						rowInfo={{
							timeSheetId: params.row.timeSheetId,
							driverId: params.row.driverId,
							driverName: params.row.driverName,
							timesheetDate: params.row.timesheetDate,
							totalServices: params.row.totalServices,
							comments: params.row.comments,
							status: params.row.status,
						}}
					/>
				),
			},
		],
		[],
	)

	if (isLoadingBranches && isFetchingTimesheet && allItems.length === 0) {
		return (
			<Box
				component={'div'}
				style={{ display: 'flex', justifyContent: 'center', width: '100%', marginBlock: 'auto' }}
			>
				<CircularProgress sx={{ color: 'var(--primary)' }} />
			</Box>
		)
	}

	if (isError && !isFetchingTimesheetsFiltered) {
		return (
			<Box
				component={'div'}
				style={{ width: '100%', marginBottom: '14px' }}
			>
				<Alert
					severity='warning'
					sx={{
						fontFamily: 'var(--font-family)',
						fontSize: '14px',
						fontWeight: 500,
						letterSpacing: '0.15px',
						lineHeight: '150%',
						height: '37px',
						padding: '0 16px',
					}}
					action={
						<Button
							sx={{ padding: '0', alignItems: 'center', fontFamily: 'var(--font-family)' }}
							color='inherit'
							size='small'
							onClick={() => refetch()}
						>
							TENTAR NOVAMENTE
						</Button>
					}
				>
					Os dados não foram carregados corretamente
				</Alert>
			</Box>
		)
	}

	if (timesheetsFilteredPageDetail?.totalResults === 0) {
		return (
			<Box
				component={'div'}
				style={{ width: '100%' }}
			>
				<Alert
					severity='info'
					sx={{
						fontFamily: 'var(--font-family)',
						fontSize: '14px',
						fontWeight: 500,
						letterSpacing: '0.15px',
						lineHeight: '150%',
						padding: '0 16px',
					}}
				>
					Neste momento não existem Folhas de Ponto filtradas para exibir. Ajuste os filtros para encontrar as folhas de
					ponto desejadas.
				</Alert>
			</Box>
		)
	}

	return (
		<Box
			ref={gridContainerRef}
			sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}
		>
			<DataGridPro
				aria-label='Timesheet table'
				apiRef={apiRef}
				rows={allItems}
				columns={columns}
				getRowId={row => row.timeSheetId}
				sortingMode='server'
				sortModel={sortModel}
				onSortModelChange={handleSortModelChange}
				rowCount={timesheetsFilteredPageDetail?.totalResults ?? 0}
				hideFooter
				disableColumnMenu
				getRowClassName={params => (params.row.timeSheetId === Number(id) ? 'Mui-selected' : '')}
				onRowClick={params => handleNewTimeSheet(params.row)}
				sx={{
					maxHeight: '100%',
					border: 'none',
					overflow: 'auto',
					fontFamily: 'var(--font-family)',
					'& .MuiDataGrid-row': {
						cursor: 'pointer',
						'&:focus': {
							outline: 'none',
						},
						'&.Mui-selected': {
							outline: 'none',
						},
					},
					'& .MuiDataGrid-columnSeparator': {
						display: 'none',
					},
					'& .MuiDataGrid-columnHeaderTitle': {
						fontWeight: 600,
						fontSize: '0.75rem',
						color: 'var(--primary-light)',
						'&:focus': {
							outline: 'none',
						},
						'&.Mui-selected': {
							outline: 'none',
						},
					},
					'& .MuiDataGrid-cell': {
						'--height': '48px',
						borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
						borderTop: 'none',
						display: 'flex',
						alignItems: 'center',
					},
					'& .MuiDataGrid-columnHeader:first-child': {
						paddingLeft: '5px',
					},
				}}
			/>
		</Box>
	)
}

export default TableMotoristas
